/**
 * Professional Translation Loader
 * 
 * Loads all namespace JSON files from locale directories and combines them.
 * This approach keeps translations organized, maintainable, and scalable.
 */

import enCommon from './locales/en/common.json';
import enPOS from './locales/en/pos.json';
import enCart from './locales/en/cart.json';
import enItems from './locales/en/items.json';
import enCustomers from './locales/en/customers.json';
import enDiscounts from './locales/en/discounts.json';
import enModals from './locales/en/modals.json';
import enTill from './locales/en/till.json';
import enMessages from './locales/en/messages.json';

// Import Spanish translations
import esCommon from './locales/es/common.json';
import esPOS from './locales/es/pos.json';
import esCart from './locales/es/cart.json';
import esItems from './locales/es/items.json';
import esCustomers from './locales/es/customers.json';
import esDiscounts from './locales/es/discounts.json';
import esModals from './locales/es/modals.json';
import esTill from './locales/es/till.json';
import esMessages from './locales/es/messages.json';

// Import French translations
import frCommon from './locales/fr/common.json';
import frPOS from './locales/fr/pos.json';
import frCart from './locales/fr/cart.json';
import frItems from './locales/fr/items.json';
import frCustomers from './locales/fr/customers.json';
import frDiscounts from './locales/fr/discounts.json';
import frModals from './locales/fr/modals.json';
import frTill from './locales/fr/till.json';
import frMessages from './locales/fr/messages.json';

// Import Arabic translations
import arCommon from './locales/ar/common.json';
import arPOS from './locales/ar/pos.json';
import arCart from './locales/ar/cart.json';
import arItems from './locales/ar/items.json';
import arCustomers from './locales/ar/customers.json';
import arDiscounts from './locales/ar/discounts.json';
import arModals from './locales/ar/modals.json';
import arTill from './locales/ar/till.json';
import arMessages from './locales/ar/messages.json';

// ============================================================================
// ENGLISH (Source of Truth - Generates Types)
// ============================================================================
const en = {
  common: enCommon,
  pos: enPOS,
  cart: enCart,
  items: enItems,
  customers: enCustomers,
  discounts: enDiscounts,
  modals: enModals,
  till: enTill,
  messages: enMessages,
} as const;

// ============================================================================
// SPANISH
// ============================================================================
const es = {
  common: esCommon,
  pos: esPOS,
  cart: esCart,
  items: esItems,
  customers: esCustomers,
  discounts: esDiscounts,
  modals: esModals,
  till: esTill,
  messages: esMessages,
} as const;

// ============================================================================
// FRENCH
// ============================================================================
const fr = {
  common: frCommon,
  pos: frPOS,
  cart: frCart,
  items: frItems,
  customers: frCustomers,
  discounts: frDiscounts,
  modals: frModals,
  till: frTill,
  messages: frMessages,
} as const;

// ============================================================================
// ARABIC
// ============================================================================
const ar = {
  common: arCommon,
  pos: arPOS,
  cart: arCart,
  items: arItems,
  customers: arCustomers,
  discounts: arDiscounts,
  modals: arModals,
  till: arTill,
  messages: arMessages,
} as const;

// ============================================================================
// EXPORTS & TYPE GENERATION
// ============================================================================

/**
 * All translations organized by language code
 */
export const translations = {
  en,
  es,
  fr,
  ar,
} as const;

/**
 * Supported language codes
 */
export type SupportedLanguage = keyof typeof translations;

/**
 * Type-safe translation key paths
 * Examples: 'common.save', 'cart.checkout', 'till.stats.totalSales'
 */
type DeepKeys<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends object
          ? `${K}.${DeepKeys<T[K]>}`
          : K
        : never;
    }[keyof T]
  : never;

export type TranslationKey = DeepKeys<typeof en>;

/**
 * Get a value from a nested object using a dot-notation path
 */
function getNestedValue(obj: any, path: string): string {
  return path.split('.').reduce((current, key) => current?.[key], obj) || path;
}

/**
 * Type-safe translation function
 * @param key - Translation key (e.g., 'cart.checkout')
 * @param params - Optional parameters for interpolation (e.g., {count: 5})
 * @param language - Language code (defaults to current language)
 */
export function translate(
  key: TranslationKey,
  params?: Record<string, string | number>,
  language: SupportedLanguage = 'en'
): string {
  let translation = getNestedValue(translations[language], key);

  // Fallback to English if not found
  if (typeof translation !== 'string') {
    translation = getNestedValue(translations.en, key);
  }

  // Fallback to key itself if still not found
  if (typeof translation !== 'string') {
    translation = key;
  }

  // Handle interpolation (e.g., "{{count}} items")
  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      translation = translation.replace(`{{${paramKey}}}`, String(paramValue));
    });
  }

  return translation;
}

/**
 * Export default for convenience
 */
export default translations;

